/**
 * DrilldownContext — Global Zoom In/Out navigation for OmniTower.
 *
 * Provides a 3-tier zoom model layered on top of the existing LayerContext:
 *
 *   Level 1 (Macro)  — Regional / Command Center KPIs
 *   Level 2 (Meso)   — District / Store-specific performance
 *   Level 3 (Micro)  — Individual Alerts, Tasks, or Forensic events
 *
 * Each zoom transition preserves a breadcrumb trail so the user
 * can "zoom out" (roll-up) from any deep link.
 *
 * @module contexts/DrilldownContext
 */

import React, { createContext, useContext, useMemo, useCallback, useState, useRef } from 'react';
import type { Page } from '@/types';

// ── Types ──────────────────────────────────────────────────────

export type ZoomLevel = 'macro' | 'meso' | 'micro';

export interface DrilldownBreadcrumb {
  label: string;
  page: Page;
  storeId?: string;
  zoomLevel: ZoomLevel;
}

export interface DrilldownState {
  /** Current zoom level */
  zoomLevel: ZoomLevel;
  /** Full breadcrumb trail for roll-up navigation */
  trail: DrilldownBreadcrumb[];
  /** Zoom in one level deeper */
  zoomIn: (target: { page: Page; storeId?: string; label: string }) => void;
  /** Zoom out to a specific breadcrumb (or one level up if no index) */
  zoomOut: (index?: number) => void;
  /** Reset zoom to macro (top) level */
  resetZoom: () => void;
  /** Get the zoom level for a given page */
  getPageZoomLevel: (page: Page) => ZoomLevel;
}

// ── Zoom level mapping ────────────────────────────────────────

const PAGE_ZOOM_MAP: Record<Page, ZoomLevel> = {
  'command-center': 'macro',
  executive: 'macro',
  operations: 'macro',
  overview: 'macro',
  district: 'meso',
  dashboard: 'meso',
  floor: 'meso',
  alerts: 'micro',
  cases: 'micro',
  tasks: 'micro',
  forensics: 'micro',
  'cashier-performance': 'micro',
  'auditor-home': 'meso',
  reports: 'macro',
  admin: 'macro',
  'risk-tower': 'macro',
};

const ZOOM_LABELS: Record<Page, string> = {
  'command-center': 'Centro de Mando',
  executive: 'Vista Ejecutiva',
  operations: 'Operaciones',
  overview: 'Resumen',
  district: 'Distrito',
  dashboard: 'Tienda',
  floor: 'Piso',
  alerts: 'Alertas',
  cases: 'Investigaciones',
  tasks: 'Tareas',
  forensics: 'Forense',
  'cashier-performance': 'POS',
  'auditor-home': 'Auditoría',
  reports: 'Reportes',
  admin: 'Ajustes',
  'risk-tower': 'Torre de Riesgo',
};

// ── Context ────────────────────────────────────────────────────

const DrilldownContext = createContext<DrilldownState | null>(null);

interface DrilldownProviderProps {
  children: React.ReactNode;
  activePage: Page;
  storeId?: string;
  storeName?: string;
  onNavigate: (page: Page, opts?: { storeId?: string }) => void;
  /** Role check — crumbs for pages the user can't open are left out of the trail. */
  canAccess?: (page: Page) => boolean;
}

export const DrilldownProvider: React.FC<DrilldownProviderProps> = ({
  children,
  activePage,
  storeId,
  storeName,
  onNavigate,
  canAccess,
}) => {
  // Track the navigation trail for roll-up
  const [trailOverride, setTrailOverride] = useState<DrilldownBreadcrumb[] | null>(null);
  // Flag to distinguish zoom-triggered navigations from external ones (nav rail, bottom nav)
  const isZoomNavRef = useRef(false);

  const zoomLevel = PAGE_ZOOM_MAP[activePage] ?? 'meso';

  // Auto-generate breadcrumb trail based on current page context
  const autoTrail = useMemo<DrilldownBreadcrumb[]>(() => {
    const crumbs: DrilldownBreadcrumb[] = [];

    // Always start with macro root
    if (zoomLevel === 'macro') {
      crumbs.push({
        label: ZOOM_LABELS[activePage] ?? 'Empresa',
        page: activePage,
        zoomLevel: 'macro',
      });
    } else {
      // For meso/micro, show Command Center or Executive as the root
      crumbs.push({
        label: 'Centro de Mando',
        page: 'command-center',
        zoomLevel: 'macro',
      });
    }

    // Meso level: add district or store
    if (zoomLevel === 'meso' || zoomLevel === 'micro') {
      if (activePage === 'district' || storeId) {
        crumbs.push({
          label: 'Distrito',
          page: 'district',
          zoomLevel: 'meso',
        });
      }
      if (storeId) {
        crumbs.push({
          label: storeName ?? 'Tienda',
          page: 'dashboard',
          storeId,
          zoomLevel: 'meso',
        });
      }
    }

    // Micro level: add the functional module
    if (zoomLevel === 'micro') {
      crumbs.push({
        label: ZOOM_LABELS[activePage] ?? activePage,
        page: activePage,
        storeId,
        zoomLevel: 'micro',
      });
    }

    // Never offer a crumb that lands on "Acceso Restringido" (keep the current page)
    return canAccess
      ? crumbs.filter((c, i) => i === crumbs.length - 1 || canAccess(c.page))
      : crumbs;
  }, [activePage, storeId, storeName, zoomLevel, canAccess]);

  const trail = trailOverride ?? autoTrail;

  // Zoom in deeper
  const zoomIn = useCallback(
    (target: { page: Page; storeId?: string; label: string }) => {
      const targetZoom = PAGE_ZOOM_MAP[target.page] ?? 'meso';
      const newCrumb: DrilldownBreadcrumb = {
        label: target.label,
        page: target.page,
        storeId: target.storeId,
        zoomLevel: targetZoom,
      };

      setTrailOverride(prev => {
        const base = prev ?? autoTrail;
        // Append only if we're going deeper
        return [...base, newCrumb];
      });

      isZoomNavRef.current = true;
      onNavigate(target.page, { storeId: target.storeId });
    },
    [autoTrail, onNavigate],
  );

  // Zoom out to a specific breadcrumb index, or one level up
  const zoomOut = useCallback(
    (index?: number) => {
      if (index !== undefined && index >= 0 && index < trail.length) {
        const target = trail[index];
        // Truncate trail to this point
        setTrailOverride(trail.slice(0, index + 1));
        isZoomNavRef.current = true;
        onNavigate(target.page, { storeId: target.storeId });
      } else {
        // Go up one level in the trail
        if (trail.length > 1) {
          const parent = trail[trail.length - 2];
          setTrailOverride(trail.slice(0, -1));
          isZoomNavRef.current = true;
          onNavigate(parent.page, { storeId: parent.storeId });
        }
      }
    },
    [trail, onNavigate],
  );

  // Reset to macro
  const resetZoom = useCallback(() => {
    setTrailOverride(null);
    onNavigate('command-center');
  }, [onNavigate]);

  // Helper to get zoom level for any page
  const getPageZoomLevel = useCallback(
    (page: Page): ZoomLevel => PAGE_ZOOM_MAP[page] ?? 'meso',
    [],
  );

  // Reset trail override only when page changes externally (e.g. nav rail click).
  // Zoom-triggered navigations set isZoomNavRef so we skip the reset.
  React.useEffect(() => {
    if (isZoomNavRef.current) {
      isZoomNavRef.current = false;
      return;
    }
    setTrailOverride(null);
  }, [activePage]);

  const value: DrilldownState = {
    zoomLevel,
    trail,
    zoomIn,
    zoomOut,
    resetZoom,
    getPageZoomLevel,
  };

  return (
    <DrilldownContext.Provider value={value}>
      {children}
    </DrilldownContext.Provider>
  );
};

export function useDrilldown(): DrilldownState {
  const ctx = useContext(DrilldownContext);
  if (!ctx) {
    throw new Error('useDrilldown must be used within <DrilldownProvider>');
  }
  return ctx;
}

export default DrilldownContext;
